export default function WhyMarketMe() {
  const benefits = [
    {
      icon: 'ri-flashlight-line',
      title: 'Fast Turnaround',
      description: 'Most websites launched within 2-3 weeks. No endless delays or missed deadlines.',
      iconBg: 'from-orange-500 to-red-500',
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Clear Monthly Pricing',
      description: 'Transparent, predictable costs. No hidden fees or surprise invoices.',
      iconBg: 'from-green-500 to-emerald-600',
    },
    {
      icon: 'ri-door-open-line',
      title: 'No Long-Term Lock-Ins',
      description: 'Cancel anytime with 30 days notice. We earn your business every month.',
      iconBg: 'from-yellow-500 to-orange-500',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Ongoing Support',
      description: 'We don\'t disappear after launch. Continuous updates and optimisation included.',
      iconBg: 'from-pink-500 to-rose-500',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Growth-Focused Design',
      description: 'Built to scale with your business. Add features and pages as you grow.',
      iconBg: 'from-indigo-500 to-purple-500',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Security & Performance',
      description: 'Fast loading, mobile-optimised, and secure. Your site stays healthy 24/7.',
      iconBg: 'from-teal-500 to-cyan-500',
    },
  ];

  const integrations = [
    { name: 'Mailchimp', icon: 'ri-mail-send-line' },
    { name: 'Shopify', icon: 'ri-shopping-bag-3-line' },
    { name: 'Analytics', icon: 'ri-line-chart-line' },
    { name: 'Maps', icon: 'ri-map-pin-line' },
    { name: 'Stripe', icon: 'ri-bank-card-line' },
    { name: 'Facebook', icon: 'ri-facebook-fill' },
    { name: 'Instagram', icon: 'ri-instagram-line' },
    { name: 'LinkedIn', icon: 'ri-linkedin-fill' },
    { name: 'Twitter', icon: 'ri-twitter-x-line' },
    { name: 'Zapier', icon: 'ri-flashlight-line' },
    { name: 'HubSpot', icon: 'ri-customer-service-line' },
    { name: 'Slack', icon: 'ri-slack-line' },
    { name: 'WordPress', icon: 'ri-wordpress-line' },
    { name: 'Wix', icon: 'ri-window-line' },
    { name: 'PayPal', icon: 'ri-paypal-line' },
    { name: 'YouTube', icon: 'ri-youtube-line' },
  ];

  return (
    <section className="bg-[#121212] py-20 md:py-32">
      <div className="max-w-[1300px] mx-auto px-4 md:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">Why Choose Market Me</h2>
          <div className="w-16 md:w-20 h-1 bg-gradient-to-r from-[#8b2bd9] to-[#d946ef] mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 mb-16 md:mb-20">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-[#1a1a1a] rounded-[16px] md:rounded-[20px] p-6 md:p-10 transition-all duration-300 min-h-[220px] md:min-h-[280px]"
            >
              <div className={`w-14 h-14 bg-gradient-to-r ${benefit.iconBg} rounded-xl flex items-center justify-center mb-4 md:mb-6`}>
                <i className={`${benefit.icon} text-white text-2xl`}></i>
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-white mb-3 md:mb-4">{benefit.title}</h3>
              <p className="text-white/60 leading-relaxed text-sm md:text-base">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* Integrations Section */}
        <div className="mt-16 sm:mt-20">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-purple-400 text-xs sm:text-sm font-medium mb-2 uppercase tracking-wider">
              Seamless Connections
            </p>
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3 sm:mb-4">
              Integrations Compatible With Your Website
            </h3>
            <p className="text-gray-300 text-sm sm:text-base">
              And many more integrations to power your business
            </p>
          </div>

          {/* Scrolling Integrations */}
          <div className="relative overflow-hidden">
            {/* Gradient overlays */}
            <div className="absolute left-0 top-0 w-8 sm:w-20 h-full bg-gradient-to-r from-[#121212] to-transparent z-10 pointer-events-none"></div>
            <div className="absolute right-0 top-0 w-8 sm:w-20 h-full bg-gradient-to-l from-[#121212] to-transparent z-10 pointer-events-none"></div>
            
            <div className="flex animate-scroll-continuous">
              {/* First set of integrations */}
              {integrations.map((integration, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 flex flex-col items-center justify-center mx-2 sm:mx-4 p-2 sm:p-4 bg-gray-800/30 border border-gray-700/30 rounded-lg transition-all duration-300 w-20 h-14 sm:w-32 sm:h-20 pointer-events-none"
                >
                  <i className={`${integration.icon} text-base sm:text-xl text-gray-400 transition-colors duration-300 mb-1 sm:mb-2`}></i>
                  <span className="text-xs sm:text-sm text-gray-400 transition-colors duration-300 whitespace-nowrap text-center">
                    {integration.name}
                  </span>
                </div>
              ))}
              
              {/* Duplicate set for seamless scrolling */}
              {integrations.map((integration, index) => (
                <div
                  key={`duplicate-${index}`}
                  className="flex-shrink-0 flex flex-col items-center justify-center mx-2 sm:mx-4 p-2 sm:p-4 bg-gray-800/30 border border-gray-700/30 rounded-lg transition-all duration-300 w-20 h-14 sm:w-32 sm:h-20 pointer-events-none"
                >
                  <i className={`${integration.icon} text-base sm:text-xl text-gray-400 transition-colors duration-300 mb-1 sm:mb-2`}></i>
                  <span className="text-xs sm:text-sm text-gray-400 transition-colors duration-300 whitespace-nowrap text-center">
                    {integration.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scroll-continuous {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .animate-scroll-continuous {
          animation: scroll-continuous 30s linear infinite;
        }

        @media (min-width: 640px) {
          .animate-scroll-continuous {
            animation: scroll-continuous 40s linear infinite;
          }
        }
      `}</style>
    </section>
  );
}